import {Anchor, Button, Group, Popover, Stack, Text, UnstyledButton} from '@mantine/core'
import {notifications} from '@mantine/notifications'
import {IconAlertTriangle} from '@tabler/icons-react'
import {Link} from '@tanstack/react-router'

import type {PickupToConfirm} from './schemas'
import {useConfirmPickupReady, usePickupsToConfirm} from './use-work'

function PickupToConfirmRow({
    pickup,
    onConfirmed,
}: {
    pickup: PickupToConfirm
    onConfirmed?: () => void
}) {
    const confirm = useConfirmPickupReady(pickup.projectId, pickup.jobId)

    async function handleConfirm() {
        try {
            await confirm.mutateAsync()
            notifications.show({
                color: 'green',
                title: 'Pickup confirmed',
                message: pickup.displayId,
            })
            onConfirmed?.()
        } catch (caught) {
            notifications.show({
                color: 'red',
                title: 'Could not confirm the pickup',
                message: caught instanceof Error ? caught.message : 'Try again.',
            })
        }
    }

    return (
        <Group justify="space-between" wrap="nowrap" gap="sm" data-testid="pickup-to-confirm">
            <Stack gap={0} style={{flex: 1, minWidth: 0}}>
                <Anchor
                    size="sm"
                    renderRoot={(props) => (
                        <Link {...props} to="/jobs/$jobId" params={{jobId: pickup.jobId}} />
                    )}
                >
                    {pickup.displayId}: {pickup.name}
                </Anchor>
                <Text size="xs" c="dimmed">
                    {pickup.customerName}
                    {pickup.repName ? ` · rep ${pickup.repName}` : ''}
                </Text>
            </Stack>
            {pickup.canConfirm ? (
                <Button
                    size="compact-xs"
                    variant="light"
                    style={{flexShrink: 0}}
                    loading={confirm.isPending}
                    onClick={() => void handleConfirm()}
                >
                    Confirm pickup
                </Button>
            ) : null}
        </Group>
    )
}

export function PickupsToConfirmLink({
    yardId,
    onConfirmed,
}: {
    yardId: string | null
    onConfirmed?: () => void
}) {
    const {data} = usePickupsToConfirm(yardId)
    const pickups = data?.pickups ?? []
    if (pickups.length === 0) {
        return null
    }

    const label = `${pickups.length} ${pickups.length === 1 ? 'pickup' : 'pickups'} tomorrow not confirmed`

    return (
        <Popover width={420} position="bottom-start" withArrow shadow="md">
            <Popover.Target>
                <UnstyledButton data-testid="pickups-to-confirm-link">
                    <Group gap={4}>
                        <IconAlertTriangle size={16} color="var(--mantine-color-orange-6)" />
                        <Text size="sm" c="orange" td="underline">
                            {label}
                        </Text>
                    </Group>
                </UnstyledButton>
            </Popover.Target>
            <Popover.Dropdown maw="calc(100vw - 2rem)">
                <Stack gap="sm">
                    <Text size="xs" c="dimmed">
                        Confirm the equipment is ready for pickup. If it was released early, use
                        Pickup sooner on the job.
                    </Text>
                    {pickups.map((pickup) => (
                        <PickupToConfirmRow
                            key={pickup.jobId}
                            pickup={pickup}
                            onConfirmed={onConfirmed}
                        />
                    ))}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    )
}
